import {
	ConnectionManager,
	ProductData,
	SceneObject,
	TransformVector,
} from '@ikea-aoa/component-view-3d';
import { CoverItem } from 'sofa-reborn-react';
import { ModuleConnectionManager } from '../../../util/SceneUtils/ModuleConnectionManager';
import { ObjectManager } from '../ObjectManager';

type Props = {
	products: ProductData;
	cover: CoverItem;
};
export class CushionManager {
	private connectionManager: ConnectionManager;
	private moduleManager: ModuleConnectionManager;
	private sceneItems: Map<string, SceneObject>;

	constructor(private objectManager: ObjectManager, private props: Props) {
		this.connectionManager = objectManager.connectionManager;
		this.moduleManager = objectManager.moduleManager;
		this.sceneItems = objectManager.sceneItems;
	}

	public updateProps(props: Props): void {
		this.props = props;
	}

	public createAutoCushionObjects(): void {
		const backrestsMissingCushion = this.getBackrestsMissingCushion();
		const cushionArticleId = this.getCushionArticleId(backrestsMissingCushion);
		const cushionsToFadeIn: SceneObject[] = [];

		if (cushionArticleId) {
			backrestsMissingCushion.forEach((backrestObject) => {
				const pos = TransformVector.zero();

				const backrestModel = this.connectionManager.getModelData(backrestObject.articleId);
				const cushionModel = this.connectionManager.getModelData(cushionArticleId);
				if (backrestModel && cushionModel) {
					const relation = this.connectionManager.getRelations(cushionModel, backrestModel)[0];

					const cushion = this.props.products[cushionArticleId];
					const cushionSceneObject = this.objectManager.createObject(
						cushionArticleId,
						pos,
						false,
						cushion.filter.color
					);
					if (cushionSceneObject && cushionSceneObject.node) {
						cushionSceneObject.node.visibility = 0;
						this.sceneItems.set(cushionSceneObject.id, cushionSceneObject);
						this.moduleManager.connect(cushionSceneObject, backrestObject, relation);

						cushionsToFadeIn.push(cushionSceneObject);
					}
				}
			});

			cushionsToFadeIn.sort((cushionObject1, cushionObject2) => {
				const position1 = this.connectionManager.getWorldPosition(cushionObject1);
				const position2 = this.connectionManager.getWorldPosition(cushionObject2);
				if (Math.floor(position1.x as number) === Math.floor(position2.x as number)) {
					return (position2.z as number) - (position1.z as number);
				}
				return (position2.x as number) - (position1.x as number);
			});

			this.fadeInCushionsSequential(cushionsToFadeIn, 0);
		} else {
			throw 'SceneContainer: createAutoCushionObjects(): cushionArticleId does not exist';
		}
	}

	public getBackrestsMissingCushion(): SceneObject[] {
		const backrestsMissingCushion: SceneObject[] = [];
		this.sceneItems.forEach((sceneItem) => {
			const family = this.connectionManager.getFamily(sceneItem.articleId);
			if (!family || !family.includes('BACKREST')) {
				return;
			}

			const children = this.connectionManager.getChildNotStandaloneObjects(sceneItem);
			if (children.length === 0) {
				backrestsMissingCushion.push(sceneItem);
			}
		});

		return backrestsMissingCushion;
	}

	private fadeInCushionsSequential(cushionsArray: SceneObject[], index: number): void {
		if (cushionsArray.length > 0) {
			setTimeout(() => {
				let currentVisibility = 0.0;
				const fadeInInterval = setInterval(() => {
					currentVisibility += 0.1;
					if (Math.floor(currentVisibility) === 1) {
						clearInterval(fadeInInterval);
					} else {
						const node = cushionsArray[index].node;
						if (node) {
							node.visibility = currentVisibility;
						} else {
							throw 'SceneContainer: fadeInCushionsSequential(): node does not exist';
						}
					}
				}, 25);

				if (cushionsArray.length > index + 1) {
					this.fadeInCushionsSequential(cushionsArray, index + 1);
				}
			}, 250);
		}
	}

	private getCushionArticleId(cushions: SceneObject[]): string | null {
		const cushionArticleId = Object.keys(this.props.products).find((productKey) => {
			const product = this.props.products[productKey];

			return (
				this.connectionManager.getFamily(product.productId) === 'CUSHION' &&
				product.filter.color === this.props.cover.coverName
			);
		});

		return cushionArticleId === undefined ? this.getValidCushionId(cushions) : cushionArticleId;
	}

	private getValidCushionId(cushions: SceneObject[]): string {
		let cushionArticleId;
		Object.values(cushions).forEach((value) => {
			if (value.parentId) {
				const parentId = this.sceneItems.get(value.parentId)?.articleId;

				if (parentId) {
					const parentObject = this.props.products[parentId];
					let validCushion = Object.values(this.props.products).filter(
						(product) =>
							product.filter.color === parentObject.filter.color &&
							product.filter.family === 'CUSHION'
					);

					if (!validCushion.length) {
						validCushion = Object.values(this.props.products).filter(
							(product) => product.filter.family === 'CUSHION'
						);
					}

					cushionArticleId = validCushion[0].productId;
				}
			}
		});

		return cushionArticleId;
	}
}
